/**
 * ⚖ A-11 turn-end provenance hydration — the matcher.
 *
 * The LIVE wire cannot carry a provider (D5 strips tool results from
 * `session.tool` frames; `meta` is built from ARGS at call start, before any
 * provider has served anything — both core-owned, measured in
 * `ai-docs/a11-provider-badge-emit-miniplan.md` §5.1). So a live-finalized
 * turn learns its provenance from the DURABLE row: at turn end the hook
 * re-reads `lx.chat_history`, finds the assistant row for the finalized
 * runId, and this module matches its trail entries onto the live `tool_use`
 * blocks the message already renders.
 *
 * ## Why (name, ordinal) matching is sound — and only for these tools
 *
 * Trail entries carry no toolCallId (D5 §2 — the store's shape is
 * `{name, label?, target?, outcome, provider?, path?}`), so identity must be
 * reconstructed. For the badge-bearing tools this is safe: only PLAN tools
 * mutate a live block in place (`useChatStore.upsertLiveToolBlock`); the
 * search/fetch tools append one block per invocation, in call order — the
 * same order the trail accumulates in (`before_tool_call` appends). So the
 * k-th `web_search` block IS the k-th `web_search` trail entry.
 *
 * ## Honest-absence rule
 *
 * A COUNT MISMATCH for a name yields no attribution for that name at all —
 * never a nearest-match. A shifted badge would assert the wrong provider
 * with full confidence, which is worse than no badge (the same splice rule
 * as the markdown chip indexer: when two lengths disagree, render nothing
 * clever).
 */
import type { ContentBlock } from "@/types/content-blocks";
import type { ToolTrailEntry } from "@/types/gateway";

/** The tools whose trail entries may carry `provider` (emit miniplan §3).
 *  A closed set HERE is correct — it mirrors the emitter's derivation table,
 *  not a server vocabulary: any other tool never gets the fields at all. */
export const ATTRIBUTED_TOOLS: ReadonlySet<string> = new Set([
  "web_search",
  "web_fetch",
  "lx_browser_fetch",
]);

export type TrailAttribution = Record<string, { provider: string; path?: string }>;

/** Does this finalized message have anything a badge could ever attach to?
 *  Used to skip the hydration fetch entirely for turns with no web calls. */
export function hasAttributableBlocks(blocks: ContentBlock[]): boolean {
  return blocks.some((b) => b.type === "tool_use" && ATTRIBUTED_TOOLS.has(b.name));
}

/**
 * Match the persisted trail onto the live blocks, per the rules above.
 * Returns `{}` when there is nothing to say — the store treats that as
 * "leave the message untouched", so absence stays absent.
 */
export function buildTrailAttribution(
  blocks: ContentBlock[],
  trail: ToolTrailEntry[],
): TrailAttribution {
  const attribution: TrailAttribution = {};
  for (const name of ATTRIBUTED_TOOLS) {
    const liveBlocks = blocks.filter(
      (b): b is Extract<ContentBlock, { type: "tool_use" }> =>
        b.type === "tool_use" && b.name === name,
    );
    const entries = trail.filter((e) => e.name === name);
    // Count mismatch ⇒ the reconstruction is not trustworthy for this name.
    // Skip it wholesale — honest absence, never shifted attribution.
    if (liveBlocks.length !== entries.length) continue;
    for (let k = 0; k < liveBlocks.length; k++) {
      const id = liveBlocks[k].id;
      const provider = entries[k].provider;
      // A block without an id has nothing to hang a badge on; an entry
      // without a provider has no badge to hang. Both skip silently.
      if (!id || !provider) continue;
      attribution[id] = {
        provider,
        ...(entries[k].path ? { path: entries[k].path } : {}),
      };
    }
  }
  return attribution;
}
